import { Box } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IoPlayOutline } from 'react-icons/io5'
import { Button } from 'src/Common/ui/button'
import { Checkbox } from 'src/Common/ui/checkbox'
import Input from 'src/Common/ui/input'
import Textarea from 'src/Common/ui/textArea'
import { cn, inputValidation } from 'src/lib/utils'
import * as PropTypes from 'prop-types'

const ThreePointEstimate = ({ setformData, estimationArray, setEstimationArray, register, formData, onValueChange, errors, control }) => {
    const { t } = useTranslation()

    const onEstimateCheckboxValueChange = (value, data) => {
        setEstimationArray(estimationArray?.map((mapData, mapIdx) => {
            if (mapData?.id === data?.id) {
                return { ...mapData, isChecked: !mapData?.isChecked }
            } else {
                return mapData
            }
        }))
    }

    const handleCalculate = () => {
        const costEstimation = (
            Number(formData?.threePointEstimate?.optimisticCost) +
            4 * Number(formData?.threePointEstimate?.mostLikelyCost) +
            Number(formData?.threePointEstimate?.pessimisticCost)
        ) / 6

        const durationEstimation = (
            Number(formData?.threePointEstimate?.optimisticDays) +
            4 * Number(formData?.threePointEstimate?.mostLikelyDays) +
            Number(formData?.threePointEstimate?.pessimisticDays)
        ) / 6

        setformData({
            ...formData,
            threePointEstimate: {
                ...formData?.threePointEstimate,
                costEstimation: (costEstimation || 0).toFixed(2),
                durationEstimation: (durationEstimation || 0).toFixed(2)
            }
        })
    }

    const isCalculateDisabled = () => {
        const costChecked = estimationArray[0]?.isChecked
        const durationChecked = estimationArray[1]?.isChecked

        const costFieldsFilled =
            formData?.threePointEstimate?.optimisticCost &&
            formData?.threePointEstimate?.mostLikelyCost &&
            formData?.threePointEstimate?.pessimisticCost

        const durationFieldsFilled =
            formData?.threePointEstimate?.optimisticDays &&
            formData?.threePointEstimate?.mostLikelyDays &&
            formData?.threePointEstimate?.pessimisticDays

        if (costChecked && durationChecked) {
            return !(costFieldsFilled && durationFieldsFilled)
        } else if (costChecked) {
            return !costFieldsFilled
        } else if (durationChecked) {
            return !durationFieldsFilled
        }

        return true // Disabled if no checkbox is selected
    }

    return (
        <div>
            <Box display="grid" sx={{}} gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                <Box gridColumn="span 7">
                    <p className='text-muted font-bold pb-2'>Estimate Type</p>
                    <div className='flex flex-row py-4'>
                        {estimationArray?.map((mapDt, key) =>
                            <div key={key} className='flex mt-auto mb-4 pr-4'>
                                <Checkbox
                                    checked={mapDt?.isChecked}
                                    borderColor={`hsl(var(--${mapDt?.title}))`}
                                    onCheckedChange={(e) => onEstimateCheckboxValueChange(e, mapDt)}
                                    className={mapDt?.isChecked ? 'my-auto border-none' : 'my-auto'}
                                />
                                {mapDt?.title && <label className='grc-label ml-2'>{mapDt?.title}</label>}
                            </div>)}
                    </div>
                </Box>
            </Box>
            <Box display="grid" sx={{ height: '200px' }} gridTemplateColumns="repeat(12, 1fr)" gap={4}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} gridColumn="span 6">
                    <div className='relative' style={{ marginBottom: '2%' }}>
                        <Textarea className={cn('ml-[0px]')} label="Description" rules={{
                            validate: (value) => {
                                return inputValidation(value, value?.length < 251, t('This field is required'), t('Characters should be less than 250'))
                            }
                        }} register={register} value={formData?.threePointEstimate?.treePointDiscription} type="text" placeholder="Enter Description" onValueChange={onValueChange} name="treePointDiscription" errors={errors} control={control} required maxLength={250} />
                        <div className={cn('absolute bottom-[25px] right-[10px] text-muted text-[10px]', formData?.threePointEstimate?.treePointDiscription?.length > 250 ? '!text-danger' : 'text-muted')}>{formData?.threePointEstimate?.treePointDiscription?.length || 0}/250</div>
                    </div>
                </Box>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} gridColumn="span 2">
                    <div className='relative flex flex-col justify-between'>
                        {estimationArray[0]?.isChecked && <div style={{ minHeight: '85px' }}><Input errors={errors} control={control} name='optimisticCost' label={t('Optimistic Cost')} register={register} placeholder={t('Enter Optimistic Cost')} type="number" value={formData?.threePointEstimate?.optimisticCost} onValueChange={(el, name) => onValueChange(el.target.value, name)} rules={{ min: { value: 0, message: t('Value must be at least 0') }, max: { value: 999999999999, message: t('Value must not exceed 999999999999') } }} /></div>}
                        {estimationArray[1]?.isChecked && <div style={{ minHeight: '65px' }}><Input errors={errors} control={control} name='optimisticDays' label={t('Optimistic Days')} register={register} placeholder={t('Enter Optimistic Days')} type="number" value={formData?.threePointEstimate?.optimisticDays} onValueChange={(el, name) => onValueChange(el.target.value, name)} rules={{ min: { value: 0, message: t('Value must be at least 0') }, max: { value: 9999, message: t('Value must not exceed 9999 days') } }} /></div>}
                    </div>
                </Box>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} gridColumn="span 2">
                    <div className='relative flex flex-col justify-between'>
                        {estimationArray[0]?.isChecked && <div style={{ minHeight: '85px' }}><Input errors={errors} control={control} name='mostLikelyCost' label={t('Most Likely Cost')} register={register} placeholder={t('Enter Most Likely Cost')} type="number" value={formData?.threePointEstimate?.mostLikelyCost} onValueChange={(el, name) => onValueChange(el.target.value, name)} rules={{ min: { value: 0, message: t('Value must be at least 0') }, max: { value: 999999999999, message: t('Value must not exceed 999999999999') } }} /></div>}
                        {estimationArray[1]?.isChecked && <div style={{ minHeight: '65px' }}><Input errors={errors} control={control} name='mostLikelyDays' label={t('Most Likely Days')} register={register} placeholder={t('Enter Most Likely Days')} type="number" value={formData?.threePointEstimate?.mostLikelyDays} onValueChange={(el, name) => onValueChange(el.target.value, name)} rules={{ min: { value: 0, message: t('Value must be at least 0') }, max: { value: 9999, message: t('Value must not exceed 9999 days') } }} /></div>}
                    </div>
                </Box>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} gridColumn="span 2">
                    <div className='relative flex flex-col justify-between'>
                        {estimationArray[0]?.isChecked && <div style={{ minHeight: '85px' }}><Input errors={errors} control={control} name='pessimisticCost' label={t('Pessimistic Cost')} register={register} placeholder={t('Enter Pessimistic Cost')} type="number" value={formData?.threePointEstimate?.pessimisticCost} onValueChange={(el, name) => onValueChange(el.target.value, name)} rules={{ min: { value: 0, message: t('Value must be at least 0') }, max: { value: 999999999999, message: t('Value must not exceed 999999999999') } }} /></div>}
                        {estimationArray[1]?.isChecked && <div style={{ minHeight: '65px' }}><Input errors={errors} control={control} name='pessimisticDays' label={t('Pessimistic Days')} register={register} placeholder={t('Enter Pessimistic Days')} type="number" value={formData?.threePointEstimate?.pessimisticDays} onValueChange={(el, name) => onValueChange(el.target.value, name)} rules={{ min: { value: 0, message: t('Value must be at least 0') }, max: { value: 9999, message: t('Value must not exceed 9999 days') } }} /></div>}
                    </div>
                </Box>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '20px' }} gridColumn={estimationArray?.every(item => item.isChecked) ? 'span 7' : 'span 4'}>
                    {estimationArray?.some((data) => data?.isChecked === true) && <Button
                        component='label'
                        size='small'
                        onClick={handleCalculate}
                        disabled={isCalculateDisabled()} // Disable button when conditions are not met
                        className={isCalculateDisabled() ? 'opacity-50 cursor-not-allowed' : ''}
                    >
                        <span className="flex text-card items-center" style={{ fontWeight: 'bold' }}>
                            <IoPlayOutline color='hsl(var(--card))' className="mr-2" size={15} /> Calculate
                        </span>
                    </Button>}
                    {estimationArray[0]?.isChecked && <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                        <p className='text-primary font-bold pb-2'>Cost Estimation : </p>
                        <p className='text-muted font-bold break-words'>₹ {formData?.threePointEstimate?.costEstimation || 0}</p>
                    </div>}
                    {estimationArray[1]?.isChecked && <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                        <p className='text-primary font-bold pb-2'>Duration Estimation : </p>
                        <p className='text-muted font-bold break-words'>{formData?.threePointEstimate?.durationEstimation || 0}</p>
                    </div>}
                </Box>
            </Box>
        </div>
    )
}

ThreePointEstimate.prototype = {
    estimationArray: PropTypes.any,
    setEstimationArray: PropTypes.any,
    register: PropTypes.any,
    formData: PropTypes.any,
    onValueChange: PropTypes.any,
    errors: PropTypes.any,
    control: PropTypes.any
}

export default ThreePointEstimate
